import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  ClaimFilterRequestDTO,
  ClaimGenerationBatchRequestDTO,
  ClaimGenerationFilterRequestDTO,
  OverrideClaimsErrorsRequestDTO,
  PaymentFilterRequestDTO,
  PaymentInputDTO,
  PaymentPostBatchRequestDTO,
  PaymentPostFilterRequestDTO,
  RmmtFilterRequestDTO,
} from "../../pages/billing/models";
import { ConfigurationService } from "../configuration/configuration.service";

// CO-3457 Overlap Review (BDD 7.10/7.11). Bare snake_case wire row - not an
// envelope. Timestamps are ISO-8601 UTC strings. id and both assessment ids
// are numbers - the backend serializes a Java Long as a JSON number, not a
// string.
export interface VisitOverlapRow {
  id: number;
  patient_name: string;
  discipline: string;
  clinician_a: string;
  clinician_b: string;
  visit_a_assessment_id: number;
  visit_b_assessment_id: number;
  visit_a_start: string;
  visit_a_end: string;
  visit_b_start: string;
  visit_b_end: string;
  overlap_start: string;
  overlap_end: string;
  status: "OPEN" | "ACCEPTED" | "CLEARED";
  accepted_by: string;
  accepted_at: string;
}

export type VisitOverlapDisposition = "OPEN" | "ACCEPTED" | "ALL";

@Injectable()
export class BillingService {
  private readonly billingUrl: string;
  private readonly uploadUrl: string;

  constructor(public http: HttpClient, private config: ConfigurationService) {
    if (this.config.getConfig().isLocal) {
      this.billingUrl = config.getConfig().localEndpoints.billing;
    } else {
      this.billingUrl = config.getConfig().apiUrl;
    }

    //this.billingUrl = 'http://localhost:8080';
    this.uploadUrl = `${config.getConfig().apiUrl}/files/upload`;
  }

  // Account Receivable APIs
  searchFromAccountReceivable(searchDetails: any) {
    return this.http.post(
      `${this.billingUrl}/accounts-receivable/search`,
      searchDetails
    );
  }

  getAccountsReceivableConfig(configType: string) {
    return this.http.post(`${this.billingUrl}/accounts-receivable/config`, {
      accountsReceivableType: configType,
    });
  }

  processManualPaymentsToAccountReceivable(manualPayment: any) {
    return this.http.post(
      `${this.billingUrl}/accounts-receivable/manual-payment`,
      manualPayment
    );
  }

  searchPayerNamesFromAccountsReceivable(accountsReceivable: any) {
    return this.http.post(
      `${this.billingUrl}/accounts-receivable/get-payer-names`,
      accountsReceivable
    );
  }
  // CO-3390 Feature 8 — manual accounts-receivable adjustments.
  //
  // The three calls behind the adjustment entry, its void, and the claim's audited
  // history. Three endpoints rather than one because applying, voiding and reading are
  // three different iam permissions on the server; the page must not assume that holding
  // one implies the others.
  //
  // NOTE what is NOT sent: no actor and no roles. The server takes both from the
  // authorizer, never the body — a client that could name its own actor could post an
  // adjustment as somebody else, and the audit record would agree with it.
  applyAccountsReceivableAdjustment(adjustment: {
    claimId: string;
    lineId?: string;
    class: string;
    reasonCode: string;
    note?: string;
    amount: number;
    openBalance: number;
  }) {
    return this.http.post(
      `${this.billingUrl}/accounts-receivable/adjustment`,
      adjustment
    );
  }

  voidAccountsReceivableAdjustment(request: {
    claimId: string;
    entryId: string;
    reason: string;
  }) {
    return this.http.post(
      `${this.billingUrl}/accounts-receivable/adjustment/void`,
      request
    );
  }

  // Returns the claim's full history INCLUDING voided entries and the voids themselves
  // (F8.S7 shows both, neither deleted), plus canCreate / canVoid so the page renders
  // absent controls without a second copy of the permission rule in the front end.
  getAccountsReceivableAdjustments(claimId: string) {
    return this.http.get(
      `${this.billingUrl}/accounts-receivable/adjustment/${encodeURIComponent(claimId)}`
    );
  }
  // End Account Receivable APIs

  //claim functions API
  searchFromClaimFunctions(searchDetails: any) {
    return this.http.post(
      `${this.billingUrl}/claim-functions/search`,
      searchDetails
    );
  }

  searchPayerNamesFromClaimFunctions(searchDetails: any) {
    return this.http.post(
      `${this.billingUrl}/claim-functions/getPayerNames`,
      searchDetails
    );
  }

  searchPayerNamesFromClaimBatchFunctions(searchDetails: any) {
    return this.http.post(
      `${this.billingUrl}/claim-batch-functions/getPayerNames`,
      searchDetails
    );
  }

  getD9RemarksConfig() {
    return this.http.get(
      `${this.billingUrl}/claim-correction-functions/getD9RemarksConfig`
    );
  }

  resolveD9Remarks(payload: any) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/resolve/d9`,
      payload
    );
  }
  resolveNewNOEAction(payload: any) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/resolve/noe`,
      payload
    );
  }
  // Remit Upload Function API
  upload835File(file: FormData) {
    return this.http.post(`${this.billingUrl}/remit-upload`, file);
  }
  // End Remit Upload Function API

  // Edit Electronic Remit Function API
  editElectronicEremit({ postDate, s3FileURI }) {
    return this.http.post(`${this.billingUrl}/remit-upload/edit-remit/`, {
      postDate,
      s3FileURI,
    });
  }

  getClaims(pageNum?: number, pageSize?: number) {
    let params = new HttpParams();

    params = isNaN(pageNum)
      ? params
      : params.append("pageNum", pageNum.toString());
    params = isNaN(pageSize)
      ? params
      : params.append("pageSize", pageSize.toString());

    return this.http.get(`${this.billingUrl}/billing-claims`, {
      params: params,
    });
  }

  searchClaims(
    data: ClaimFilterRequestDTO,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    direction?: string
  ) {
    let params = new HttpParams();

    params = isNaN(pageNum)
      ? params
      : params.append("pageNum", pageNum.toString());
    params = isNaN(pageSize)
      ? params
      : params.append("pageSize", pageSize.toString());
    params =
      !sort || typeof sort === "undefined"
        ? params
        : params.append("sort", sort);
    params =
      !direction || typeof direction === "undefined"
        ? params
        : params.append("direction", direction);

    return this.http.post(`${this.billingUrl}/billing-claims/search`, data, {
      params: params,
    });
  }

  searchClaimsVersion(
    data: ClaimFilterRequestDTO,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    direction?: string
  ) {
    let params = new HttpParams();

    params = isNaN(pageNum)
      ? params
      : params.append("pageNum", pageNum.toString());
    params = isNaN(pageSize)
      ? params
      : params.append("pageSize", pageSize.toString());
    params =
      !sort || typeof sort === "undefined"
        ? params
        : params.append("sort", sort);
    params =
      !direction || typeof direction === "undefined"
        ? params
        : params.append("direction", direction);

    return this.http.post(
      `${this.billingUrl}/billing-claims/versions/search`,
      data,
      { params: params }
    );
  }

  updateState(data: ClaimGenerationBatchRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/update/state`,
      data
    );
  }

  generateViaBatch(data: ClaimGenerationBatchRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/generate/batch/confirm`,
      data
    );
  }

  getGenerationViaBatchSummary(data: ClaimGenerationBatchRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/generate/batch/summary`,
      data
    );
  }

  overrideErrorByBatch(data: OverrideClaimsErrorsRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/claim-functions/claims/override/errors`,
      data
    );
  }

  generateViaFilter(data: ClaimGenerationFilterRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/generate/filter/confirm`,
      data
    );
  }

  getGenerationViaFilterSummary(data: ClaimGenerationFilterRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/generate/filter/summary`,
      data
    );
  }

  getPayments(pageNum?: number, pageSize?: number) {
    let params = new HttpParams();

    params = isNaN(pageNum)
      ? params
      : params.append("pageNum", pageNum.toString());
    params = isNaN(pageSize)
      ? params
      : params.append("pageSize", pageSize.toString());

    return this.http.get(`${this.billingUrl}/payments`, { params: params });
  }

  processIncomingPayment(data: PaymentInputDTO) {
    return this.http.post(`${this.billingUrl}/payments/`, data);
  }

  postViaBatch(data: PaymentPostBatchRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/payments/post/batch/confirm`,
      data
    );
  }

  getPostViaBatchSummary(data: PaymentPostBatchRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/payments/post/batch/summary`,
      data
    );
  }

  postViaFilter(data: PaymentPostFilterRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/payments/post/filter/confirm`,
      data
    );
  }

  getPostViaFilterSummary(data: PaymentPostFilterRequestDTO) {
    return this.http.post(
      `${this.billingUrl}/payments/post/filter/summary`,
      data
    );
  }

  getFilteredPayments(
    data: PaymentFilterRequestDTO,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    direction?: string
  ) {
    let params = new HttpParams();

    params = isNaN(pageNum)
      ? params
      : params.append("pageNum", pageNum.toString());
    params = isNaN(pageSize)
      ? params
      : params.append("pageSize", pageSize.toString());
    params =
      !sort || typeof sort === "undefined"
        ? params
        : params.append("sort", sort);
    params =
      !direction || typeof direction === "undefined"
        ? params
        : params.append("direction", direction);

    return this.http.post(`${this.billingUrl}/payments/search`, data, {
      params: params,
    });
  }

  upload835(data: FormData) {
    return this.http.post(`${this.uploadUrl}`, data);
  }

  searchRmtMngt(
    data: RmmtFilterRequestDTO,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    direction?: string
  ) {
    let params = new HttpParams();

    params = isNaN(pageNum)
      ? params
      : params.append("pageNum", pageNum.toString());
    params = isNaN(pageSize)
      ? params
      : params.append("pageSize", pageSize.toString());
    params =
      !sort || typeof sort === "undefined"
        ? params
        : params.append("sort", sort);
    params =
      !direction || typeof direction === "undefined"
        ? params
        : params.append("direction", direction);

    return this.http.post(`${this.billingUrl}/files`, data, { params: params });
  }

  isValidNumber(id) {
    let newId = +id;
    return newId === 0 || Number.isNaN(newId) ? false : true;
  }

  //Month End Close
  getMECMonths(claimType: string) {
    let params = new HttpParams();
    params = params.append("claimType", claimType);
    return this.http.get(`${this.billingUrl}/month-end-close/months`, {
      params: params,
    });
  }

  searchMECMonths(searchDetails: any) {
    return this.http.post(
      `${this.billingUrl}/month-end-close/search`,
      searchDetails
    );
  }

  closeMonth(body) {
    return this.http.post(
      `${this.billingUrl}/month-end-close/close-month`,
      body
    );
  }

  // REPO-487 / I13 PR 4: async close-month + poll-status endpoints.
  // The sync closeMonth() above stays as a back-compat fallback during
  // rollout; new code should call closeMonthAsync() so the UI stops
  // blocking on the 120s API GW cap.
  //
  // Backend contract: domains/billing/docs/i13-ui-handoff.md
  closeMonthAsync(body) {
    let params = new HttpParams();
    params = params.append("async", "true");
    return this.http.post(
      `${this.billingUrl}/month-end-close/close-month`,
      body,
      { params: params }
    );
  }

  // Polls the live SFN execution state. UI polls every 3s while status
  // is RUNNING; stops on any terminal status.
  getCloseMonthStatus(executionArn: string) {
    let params = new HttpParams();
    params = params.append("executionArn", executionArn);
    return this.http.get(
      `${this.billingUrl}/month-end-close/close-month/status`,
      { params: params }
    );
  }

  // Lists all close attempts for (tenant, period) — used by the
  // resume-after-refresh flow. Returns chronologically (oldest first);
  // latest attempt is the last array entry.
  getCloseMonthInFlight(period: string) {
    let params = new HttpParams();
    params = params.append("period", period);
    return this.http.get(
      `${this.billingUrl}/month-end-close/close-month/in-flight`,
      { params: params }
    );
  }

  getClosedMonths(claimType: string) {
    let params = new HttpParams();
    params = params.append("claimType", claimType);
    return this.http.get(`${this.billingUrl}/month-end-close/closed-months`, {
      params: params,
    });
  }

  getDashboardAPI(payload) {
    //for local testing
    //return this.http.post(`http://localhost:3000/quicksight-data/${payload.parameters.tenant_id}`,payload);
    return this.http.post(
      `${this.billingUrl}/quicksight/${payload.parameters.tenant_id}`,
      payload
    );
  }

  getDashboardAPIForRegisteredUser(payload) {
    //for local testing
    //return this.http.post(`http://localhost:3000/quicksight-data/${payload.parameters.tenant_id}`,payload);
    return this.http.post(
      `${this.billingUrl}/quicksight/user/${payload.parameters.tenant_id}`,
      payload
    );
  }

  overrideIgnore(payload: any) {
    return this.http.post(
      `${this.billingUrl}/claim-correction-functions/ignore`,
      payload
    );
  }

  overrideGenerateAsOriginal(payload: any) {
    return this.http.post(
      `${this.billingUrl}/claim-correction-functions/generate-original`,
      payload
    );
  }

  // 837 Management APIs
  searchFromEightThirtySevenManagement(searchDetails: any) {
    return this.http.post(
      `${this.billingUrl}/claim-batch-functions/search`,
      searchDetails
    );
  }

  getPatientStatus() {
    return this.http.get<any[]>(
      `${this.billingUrl}/claim-functions/getPatientStatus`
    );
  }

  getOccurrenceSpan() {
    return this.http.get<any[]>(
      `${this.billingUrl}/claim-functions/getOccurrenceSpan`
    );
  }

  getConditionCodes(isCorrection: boolean) {
    let url = `${this.billingUrl}/claim-functions/getConditionCodes`;
    if (isCorrection) url += "?includeClaimCorrectionCodes=Y";
    return this.http.get<any[]>(url);
  }

  getOccurrenceCodes() {
    return this.http.get<any[]>(
      `${this.billingUrl}/claim-functions/getOccurrenceCodes`
    );
  }

  saveClaimOptionsCodes(body: any) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/claim-options`,
      body
    );
  }

  reloadClaimData(claimId: any) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/claim-options/reset/${claimId}`,
      null
    );
  }

  getCMS1500Codes() {
    return this.http.get(`${this.billingUrl}/claim-functions/getCMS1500Codes`);
  }

  searchBatchesFromClaimSubmission(searchDetails: any) {
    return this.http.post(
      `${this.billingUrl}/claim-submission/search`,
      searchDetails
    );
  }

  saveClaimNotes(body: any) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/claim-notes`,
      body
    );
  }

  editClaimNotes(body: any) {
    return this.http.post(
      `${this.billingUrl}/billing-claims/claim-notes/edit`,
      body
    );
  }

  deleteClaimNotes(id: number) {
    return this.http.delete(
      `${this.billingUrl}/billing-claims/claim-notes/${id}`
    );
  }

  // REPO-500 / HELD_AR_ACTIVITY resolution surface. Backend endpoints
  // land via REPO-497/498/501 — these wrappers just adapt the URL +
  // params to the rest of the file's pattern.
  //
  // listHeldARActivities optionally scopes to a "YYYYMMTYPE" period
  // prefix (e.g. "202604HOSPICE") via the existing begins_with SK
  // query on the MEC table. Omit to list everything for the tenant.
  listHeldARActivities(period?: string) {
    let params = new HttpParams();
    if (period) {
      params = params.append("period", period);
    }
    return this.http.get(
      `${this.billingUrl}/month-end-close/held-ar-activities`,
      { params: params }
    );
  }

  // resolveHeldARActivity flips a held row to a terminal status. For
  // REASSIGNED the caller MUST pass a targetPeriod (YYYYMM) that the
  // backend will validate as OPEN; APPROVED auto-targets and ignores
  // targetPeriod; REJECTED is a pure status flip with no replay.
  //
  // Returns the same { sk, status, resolvedBy } envelope the handler
  // emits on success. Backend maps race-losers to 409 — caller should
  // refresh the list and surface "already resolved" to the operator.
  resolveHeldARActivity(sk: string, status: string, targetPeriod?: string) {
    let body: any = { status };
    if (targetPeriod) {
      body.targetPeriod = targetPeriod;
    }
    return this.http.post(
      `${this.billingUrl}/month-end-close/held-ar-activities/${encodeURIComponent(sk)}/resolve`,
      body
    );
  }

  // CO-3457 Overlap Review (BDD 7.10). disposition scopes the search -
  // "ALL" for the review screen's default listing. Wire is a bare array,
  // not an envelope.
  searchVisitOverlaps(disposition: VisitOverlapDisposition) {
    return this.http.post<VisitOverlapRow[]>(
      `${this.billingUrl}/billing-claims/visit-overlaps/search`,
      { disposition }
    );
  }

  // CO-3457 (BDD 7.11): 200 returns the updated row (status ACCEPTED,
  // accepted_by/accepted_at populated); 404 = cross-tenant or unknown id,
  // surfaced to the caller like any other error.
  acceptVisitOverlap(id: number) {
    return this.http.post<VisitOverlapRow>(
      `${this.billingUrl}/billing-claims/visit-overlaps/${encodeURIComponent(String(id))}/accept`,
      {}
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/billing/billing-service.ts